import React, { ReactNode, useEffect } from 'react';
import {
  Modal,
  StyleSheet,
  TouchableWithoutFeedback,
  Platform,
} from 'react-native';

import { SimpleHeader } from '@components/Headers/SimpleHeader.web';
import { Box, ShadowBox } from '@components/Restyle';
import theme from '@themes/theme';

type FinalWebModalProps = {
  accessibilityLabel: string;
  visible: boolean;
  children: ReactNode;
  onClose?: () => void;
  height?: number;
  width?: number;
  title?: string;
  isFullScreen?: boolean;
  titleVariant?: keyof typeof theme.textVariants;
  titleColor?: keyof typeof theme.colors;
  marginHorizontal?: keyof typeof theme.spacing;
  marginTop?: keyof typeof theme.spacing;
  // TA-10089: backdrop-click and the header's X button both call onClose
  // directly — neither goes through a caller's own "disable Cancel while
  // loading" guard (e.g. ConfirmModal's `disabled={isLoading}`). Set this
  // while a destructive mutation is in flight so a stray click can't
  // dismiss the modal out from under it.
  closeDisabled?: boolean;
};

export const WebModal = ({
  accessibilityLabel,
  visible,
  onClose,
  children,
  height,
  width,
  title,
  isFullScreen = false,
  titleVariant,
  titleColor,
  marginHorizontal,
  marginTop,
  closeDisabled = false,
}: FinalWebModalProps) => {
  // Keep the close control rendered even while locked — SimpleHeader only
  // renders its X icon when onClose is truthy, so setting this straight to
  // undefined would make the button vanish (and shift the header layout)
  // instead of just becoming temporarily inert.
  let handleClose = onClose;
  if (onClose && closeDisabled) {
    handleClose = () => {};
  }
  useEffect(() => {
    if (Platform.OS === 'web') {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      if (visible) {
        // Reserve the scrollbar's width with padding-right before hiding it.
        // Without this, removing the body scrollbar widens the viewport and
        // reflows layouts that read window.innerWidth (useWindowDimensions,
        // Dimensions.get('window')) — e.g. Project Detail's tab bar (TA-9513).
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.body.style.overflow = originalOverflow || '';
        document.body.style.paddingRight = originalPaddingRight || '';
      }

      return () => {
        document.body.style.overflow = originalOverflow || '';
        document.body.style.paddingRight = originalPaddingRight || '';
      };
    }
  }, [visible]);
  return (
    <Modal animationType='fade' transparent={true} visible={visible}>
      <Box
        accessibilityLabel={
          accessibilityLabel ? accessibilityLabel + ' Screen' : 'Modal'
        }
        style={styles.modalContainer}>
        <TouchableWithoutFeedback
          testID='web-modal-backdrop'
          onPress={handleClose}
          style={styles.modal}>
          <Box style={styles.modal}></Box>
        </TouchableWithoutFeedback>

        <ShadowBox
          accessibilityLabel={title ? title + ' Screen' : 'Modal'}
          variant='thin'
          style={styles.popUp}
          borderRadius={isFullScreen ? 'none' : 'm'}
          overflow='hidden'
          height={height}
          width={width}>
          <>
            {!!title && (
              <SimpleHeader
                title={title}
                onClose={handleClose}
                titleVariant={titleVariant}
                titleColor={titleColor}
                marginHorizontal={marginHorizontal}
                marginTop={marginTop}
              />
            )}
            {children}
          </>
        </ShadowBox>
      </Box>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
    opacity: 0.5,
  },
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  popUp: {
    position: 'absolute',
    backgroundColor: 'white',
  },
});
